import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { ApolloError } from "@apollo/client";
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  SignalIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Card } from "../../UI/Card.tsx";
import { IconButton } from "../../UI/IconButton.tsx";
import { InfoTooltip } from "../../UI/InfoTooltip.tsx";
import { InputWithLabel } from "../../UI/InputWithLabel.tsx";
import { PrimaryButton } from "../../UI/PrimaryButton.tsx";
import { SegmentedControl } from "../../UI/SegmentedControl.tsx";
import { TextButton } from "../../UI/TextButton.tsx";
import { useToast } from "../../UI/Feedback/useToast.ts";
import { classNames } from "../../UI/Utils/classNames.ts";
import {
  OnboardingLiveListPeriod,
  OnboardingLiveSlotPlanStatus,
} from "../../gql/graphql.ts";
import { useOnboardingLives } from "../useOnboardingLives.ts";
import { ConductorSelect } from "../ConductorSelect.tsx";
import {
  formatHour,
  UserGroupAvailability,
  weekDaysInOrder,
} from "../onboardingLiveTime.ts";
import {
  addDays,
  cellKey,
  cellOf,
  daysOfWeekStartingOn,
  gridHoursOf,
  mondayOf,
  PlannedSlot,
  repeatedCells,
  SlotCell,
  SlotCellState,
  startUtcOf,
  stateOfCell,
  todayInRome,
} from "./slotPickerCells.ts";

const REPEAT_OPTIONS = [
  { value: 1, label: "No" },
  { value: 2, label: "2 sett." },
  { value: 4, label: "4 sett." },
  { value: 8, label: "8 sett." },
];

const plannedClassNameByStatus: Record<OnboardingLiveSlotPlanStatus, string> = {
  [OnboardingLiveSlotPlanStatus.Ok]:
    "bg-emerald-100 border-emerald-300 text-emerald-800",
  [OnboardingLiveSlotPlanStatus.Warning]:
    "bg-amber-100 border-amber-300 text-amber-800",
  [OnboardingLiveSlotPlanStatus.Blocked]:
    "bg-red-100 border-red-300 text-red-800",
};

const plannedStatusLabels: Record<OnboardingLiveSlotPlanStatus, string> = {
  [OnboardingLiveSlotPlanStatus.Ok]: "verificato OK",
  [OnboardingLiveSlotPlanStatus.Warning]: "verificato con avvisi",
  [OnboardingLiveSlotPlanStatus.Blocked]: "bloccato",
};

const cellClassNameByKind: Record<
  Exclude<SlotCellState["kind"], "planned">,
  string
> = {
  free: "bg-white border-slate-200 hover:border-[#7a6dd6] hover:bg-violet-50",
  selected: "bg-[#5f50bf] border-[#5f50bf] text-white",
  repeated:
    "bg-violet-50 border-dashed border-[#7a6dd6] text-[#5f50bf] hover:bg-violet-100",
  skipped:
    "bg-white border-dashed border-slate-300 text-slate-400 hover:bg-slate-50",
  existingLive: "bg-sky-50 border-sky-200 text-sky-700 cursor-not-allowed",
  unavailable: "bg-slate-100 border-transparent cursor-not-allowed",
  past: "bg-slate-50 border-transparent cursor-not-allowed",
};

function classNameOfCell(state: SlotCellState): string {
  if (state.kind !== "planned") return cellClassNameByKind[state.kind];
  return state.slot.status
    ? plannedClassNameByStatus[state.slot.status]
    : "bg-slate-200 border-slate-300 text-slate-700";
}

function describeCell(cell: SlotCell, state: SlotCellState): string {
  const when = `${dayjs(cell.day).locale("it").format("ddd D MMM")} ${formatHour(cell.hour)}`;
  switch (state.kind) {
    case "free":
      return `${when} · clic per selezionare`;
    case "selected":
      return `${when} · selezionato, clic per togliere`;
    case "repeated":
      return `${when} · ripetizione, clic per saltarla`;
    case "skipped":
      return `${when} · saltato, clic per ripristinarlo`;
    case "planned":
      return `${when} · nel piano (${state.slot.status ? plannedStatusLabels[state.slot.status] : "da verificare"}), clic per toglierlo`;
    case "existingLive":
      return `${when} · c'è già una live creata`;
    case "unavailable":
      return `${when} · fuori dalle fasce orarie del gruppo`;
    case "past":
      return `${when} · orario già passato`;
  }
}

function CellContent({ state }: { state: SlotCellState }) {
  switch (state.kind) {
    case "selected":
      return <CheckIcon className={"h-4 w-4"} />;
    case "repeated":
      return <ArrowPathIcon className={"h-4 w-4"} />;
    case "skipped":
      return <XMarkIcon className={"h-4 w-4"} />;
    case "existingLive":
      return <SignalIcon className={"h-4 w-4"} />;
    case "planned":
      return (
        <>
          <span
            className={"h-2 w-2 rounded-full bg-current group-hover:hidden"}
          />
          <XMarkIcon className={"hidden h-4 w-4 group-hover:block"} />
        </>
      );
    default:
      return null;
  }
}

function LegendItem({
  className,
  label,
}: {
  className: string;
  label: string;
}) {
  return (
    <span className={"flex items-center gap-1.5"}>
      <span className={classNames("h-3 w-3 rounded-sm border", className)} />
      {label}
    </span>
  );
}

function toggleKey(keys: Set<string>, key: string): Set<string> {
  const next = new Set(keys);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export function OnboardingLiveSlotPicker({
  userGroupId,
  availability,
  plannedSlots,
  addSlots,
  removeSlot,
}: {
  userGroupId: string;
  availability?: UserGroupAvailability | null;
  plannedSlots: (PlannedSlot & { startUtc: string })[];
  addSlots: (
    slots: { startUtc: string; teacherId?: string }[],
  ) => Promise<void>;
  removeSlot: (slotId: string) => Promise<void>;
}) {
  const now = useMemo(() => dayjs(), []);
  const today = todayInRome(now);
  const firstMonday = mondayOf(today);
  const [weekMonday, setWeekMonday] = useState(firstMonday);
  const [selected, setSelected] = useState<Map<string, SlotCell>>(new Map());
  const [skippedKeys, setSkippedKeys] = useState<Set<string>>(new Set());
  const [repeatWeeks, setRepeatWeeks] = useState(1);
  const [teacherId, setTeacherId] = useState<string>();
  const [adding, setAdding] = useState(false);
  const showToast = useToast();
  const { onboardingLives } = useOnboardingLives({
    period: OnboardingLiveListPeriod.Upcoming,
    userGroupId,
  });

  const plannedByKey = useMemo(
    () =>
      new Map(
        plannedSlots.map((slot) => [cellKey(cellOf(slot.startUtc)), slot]),
      ),
    [plannedSlots],
  );
  const existingLiveKeys = useMemo(
    () =>
      new Set(onboardingLives.map((live) => cellKey(cellOf(live.startUtc)))),
    [onboardingLives],
  );
  const selectedKeys = useMemo(() => new Set(selected.keys()), [selected]);
  const candidates = useMemo(
    () => repeatedCells(Array.from(selected.values()), repeatWeeks),
    [selected, repeatWeeks],
  );
  const repeatedKeys = useMemo(
    () => new Set(candidates.map(cellKey)),
    [candidates],
  );
  const stateOf = (cell: SlotCell) =>
    stateOfCell({
      cell,
      now,
      availability,
      plannedByKey,
      existingLiveKeys,
      selectedKeys,
      repeatedKeys,
      skippedKeys,
    });
  const cellsToAdd = candidates.filter((cell) => {
    const kind = stateOf(cell).kind;
    return kind === "selected" || kind === "repeated";
  });
  const notAddableCount = candidates.filter((cell) => {
    const kind = stateOf(cell).kind;
    return kind !== "selected" && kind !== "repeated" && kind !== "skipped";
  }).length;

  const days = daysOfWeekStartingOn(weekMonday);
  const hours = gridHoursOf(availability);

  async function withErrorToast(action: () => Promise<void>) {
    try {
      await action();
    } catch (e) {
      showToast((e as ApolloError)?.message ?? "Errore", "error");
    }
  }

  function onCellClick(cell: SlotCell, state: SlotCellState) {
    const key = cellKey(cell);
    switch (state.kind) {
      case "free":
        setSelected((current) => new Map(current).set(key, cell));
        return;
      case "selected":
        setSelected((current) => {
          const next = new Map(current);
          next.delete(key);
          return next;
        });
        return;
      case "repeated":
      case "skipped":
        setSkippedKeys((current) => toggleKey(current, key));
        return;
      case "planned":
        void withErrorToast(() => removeSlot(state.slot.slotId));
        return;
    }
  }

  function clearSelection() {
    setSelected(new Map());
    setSkippedKeys(new Set());
  }

  async function add() {
    setAdding(true);
    await withErrorToast(async () => {
      await addSlots(
        cellsToAdd.map((cell) => ({ startUtc: startUtcOf(cell), teacherId })),
      );
      clearSelection();
    });
    setAdding(false);
  }

  return (
    <Card>
      <div
        className={
          "flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        }
      >
        <div className={"flex items-center gap-1.5"}>
          <h2 className={"font-bold text-lg"}>Aggiungi slot</h2>
          <InfoTooltip
            text={
              "Clicca le celle per selezionare gli orari, scegli se ripeterli nelle settimane successive e premi Aggiungi. Le celle colorate sono già nel piano: cliccale per toglierle."
            }
          />
        </div>
        <div className={"flex items-center gap-2"}>
          <IconButton
            label={"Settimana precedente"}
            icon={ChevronLeftIcon}
            disabled={weekMonday <= firstMonday}
            disabledReason={"è la settimana in corso"}
            onClick={() => setWeekMonday(addDays(weekMonday, -7))}
          />
          <span
            className={
              "min-w-[9.5rem] text-center text-sm font-semibold text-slate-700"
            }
          >
            {dayjs(days[0]).locale("it").format("D MMM")} –{" "}
            {dayjs(days[6]).locale("it").format("D MMM YYYY")}
          </span>
          <IconButton
            label={"Settimana successiva"}
            icon={ChevronRightIcon}
            onClick={() => setWeekMonday(addDays(weekMonday, 7))}
          />
        </div>
      </div>

      <div className={"overflow-x-auto"}>
        <div
          className={"grid gap-1 min-w-[560px]"}
          style={{
            gridTemplateColumns: `3.25rem repeat(7, minmax(3.5rem, 1fr))`,
          }}
        >
          <span />
          {days.map((day, index) => (
            <span
              key={day}
              className={classNames(
                "text-center text-xs leading-4 pb-1",
                day === today
                  ? "font-semibold text-[#5f50bf]"
                  : "text-slate-500",
              )}
            >
              {weekDaysInOrder[index].label} {dayjs(day).format("D/M")}
            </span>
          ))}
          {hours.map((hour) => (
            <div key={hour} className={"contents"}>
              <span
                className={
                  "flex items-center justify-end pr-2 text-xs text-slate-500"
                }
              >
                {formatHour(hour)}
              </span>
              {days.map((day) => {
                const cell = { day, hour };
                const state = stateOf(cell);
                const isInteractive = ![
                  "existingLive",
                  "unavailable",
                  "past",
                ].includes(state.kind);
                return (
                  <button
                    key={cellKey(cell)}
                    type={"button"}
                    title={describeCell(cell, state)}
                    aria-label={describeCell(cell, state)}
                    aria-disabled={!isInteractive}
                    onClick={() => isInteractive && onCellClick(cell, state)}
                    className={classNames(
                      "group flex h-9 items-center justify-center rounded-md border transition-colors",
                      classNameOfCell(state),
                    )}
                  >
                    <CellContent state={state} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div
        className={
          "flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500"
        }
      >
        <LegendItem
          className={"bg-[#5f50bf] border-[#5f50bf]"}
          label={"Selezionato"}
        />
        <LegendItem
          className={"bg-violet-50 border-dashed border-[#7a6dd6]"}
          label={"Ripetizione"}
        />
        <LegendItem
          className={"bg-slate-200 border-slate-300"}
          label={"Nel piano, da verificare"}
        />
        <LegendItem
          className={"bg-emerald-100 border-emerald-300"}
          label={"OK"}
        />
        <LegendItem
          className={"bg-amber-100 border-amber-300"}
          label={"Con avvisi"}
        />
        <LegendItem
          className={"bg-red-100 border-red-300"}
          label={"Bloccato"}
        />
        <LegendItem
          className={"bg-sky-50 border-sky-200"}
          label={"Live già creata"}
        />
        <LegendItem
          className={"bg-slate-100 border-transparent"}
          label={"Fuori fascia o passato"}
        />
      </div>

      <div
        className={
          "grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)_auto] gap-4 items-end"
        }
      >
        <InputWithLabel
          label={"Ripeti per"}
          info={
            "Ogni orario selezionato viene ripetuto alla stessa ora per il numero di settimane scelto, inclusa quella della selezione. Clicca una cella ripetuta per saltarla, ad esempio in un giorno festivo."
          }
        >
          <SegmentedControl
            options={REPEAT_OPTIONS}
            value={repeatWeeks}
            onChange={setRepeatWeeks}
            size={"md"}
          />
        </InputWithLabel>
        <InputWithLabel
          label={"Conduttore"}
          info={
            "Automatico: viene scelto chi ha il tag Live Onboarding e ha condotto meno live nelle ultime 4 settimane. Se scegli tu un conduttore, il turno su Shiftbase viene creato anche se non era disponibile."
          }
        >
          <ConductorSelect value={teacherId} onChange={setTeacherId} />
        </InputWithLabel>
        <div className={"flex flex-col items-end gap-1.5"}>
          <span className={"text-sm text-slate-600"}>
            <span className={"font-semibold"}>{cellsToAdd.length}</span> slot da
            aggiungere
            {notAddableCount > 0
              ? ` · ${notAddableCount} già nel piano o non disponibili`
              : ""}
          </span>
          <div className={"flex items-center gap-3"}>
            {selected.size > 0 ? (
              <TextButton label={"Svuota selezione"} onClick={clearSelection} />
            ) : null}
            <PrimaryButton
              icon={PlusIcon}
              label={`Aggiungi ${cellsToAdd.length} slot`}
              loading={adding}
              disabled={cellsToAdd.length <= 0 || adding}
              disabledReason={"Seleziona almeno una cella libera"}
              onClick={add}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
